import { useState, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowUpDown, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { toast } from "@/hooks/use-toast";
import { useUsersQuery, useUpdateUserMutation, type User } from "@/hooks/queries/useUsersQuery";

// Export the type for other components that need it
export type { User };

// Mock projects for adding user to project
const mockProjects = [
  { id: "1", name: "TechCorp AB" },
  { id: "2", name: "Nordic Solutions" },
  { id: "3", name: "DataFlow Inc" },
  { id: "4", name: "Green Energy AS" },
  { id: "5", name: "FinTech Nordic" },
  { id: "6", name: "CloudBase Oy" },
  { id: "7", name: "MediTech AB" },
];

type SortKey = keyof User;
type SortDirection = "asc" | "desc";

const UsersRoles = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { data: users = [], isLoading } = useUsersQuery();
  const updateUserMutation = useUpdateUserMutation();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("lastName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterRole, setFilterRole] = useState<string>("all");
  
  // Add to project dialog state
  const [isAddToProjectOpen, setIsAddToProjectOpen] = useState(false);
  const [selectedUserForProject, setSelectedUserForProject] = useState<User | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [useGlobalDefaults, setUseGlobalDefaults] = useState(true);

  const roles = useMemo(() => [...new Set(users.map((u) => u.role))], [users]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const openAddToProjectDialog = (user: User) => {
    setSelectedUserForProject(user);
    setSelectedProjectId("");
    setUseGlobalDefaults(true);
    setIsAddToProjectOpen(true);
  };

  const handleAddToProject = () => {
    if (!selectedUserForProject || !selectedProjectId) return;

    // Get existing members for the project
    const existingMembers = JSON.parse(
      localStorage.getItem(`project_members_${selectedProjectId}`) || "[]"
    );

    // Check if user is already in project
    const alreadyExists = existingMembers.some(
      (m: { userId: string }) => m.userId === selectedUserForProject.id
    );

    if (alreadyExists) {
      toast({
        title: "User Already in Project",
        description: "This user is already a member of the selected project.",
        variant: "destructive",
      });
      return;
    }

    // Add user to project
    const newMember = {
      id: `pm-${Date.now()}`,
      userId: selectedUserForProject.id,
      projectId: selectedProjectId,
      projectRole: useGlobalDefaults ? null : selectedUserForProject.role,
      projectDepartment: useGlobalDefaults ? null : selectedUserForProject.department,
      projectRaci: useGlobalDefaults ? null : selectedUserForProject.raci,
      bm: selectedUserForProject.bm,
      permission: "Member",
      addedAt: new Date().toISOString(),
      addedBy: "Current User",
      firstName: selectedUserForProject.firstName,
      lastName: selectedUserForProject.lastName,
      email: selectedUserForProject.email,
      globalRole: selectedUserForProject.role,
      globalDepartment: selectedUserForProject.department,
      globalRaci: selectedUserForProject.raci,
    };

    localStorage.setItem(
      `project_members_${selectedProjectId}`,
      JSON.stringify([...existingMembers, newMember])
    );

    toast({
      title: t("memberAdded"),
      description: t("memberAddedDesc"),
    });

    setIsAddToProjectOpen(false);
    setSelectedUserForProject(null);
  };

  const filteredAndSortedUsers = useMemo(() => {
    let result = [...users];

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (u) =>
          u.firstName.toLowerCase().includes(term) ||
          u.lastName.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term)
      );
    }

    // Filter by role
    if (filterRole !== "all") {
      result = result.filter((u) => u.role === filterRole);
    }

    // Sort
    result.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      
      if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        return sortDirection === "asc" 
          ? (aVal === bVal ? 0 : aVal ? -1 : 1)
          : (aVal === bVal ? 0 : aVal ? 1 : -1);
      }
      
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortDirection === "asc") {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });

    return result;
  }, [users, searchTerm, filterRole, sortKey, sortDirection]);

  const SortableHeader = ({ label, sortKeyName }: { label: string; sortKeyName: SortKey }) => (
    <Button
      variant="ghost"
      onClick={() => handleSort(sortKeyName)}
      className="h-auto p-0 font-medium hover:bg-transparent"
    >
      {label}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("usersRolesTitle")}</h1>
          <p className="text-muted-foreground">{t("dashboardSubtitle")}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t("searchUsers")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("role")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allRoles")}</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role} value={role}>
                  {role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><SortableHeader label={t("firstName")} sortKeyName="firstName" /></TableHead>
                <TableHead><SortableHeader label={t("lastName")} sortKeyName="lastName" /></TableHead>
                <TableHead><SortableHeader label={t("email")} sortKeyName="email" /></TableHead>
                <TableHead><SortableHeader label={t("role")} sortKeyName="role" /></TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAddToProjectDialog(user)}
                    >
                      <UserPlus size={16} className="mr-2" />
                      {t("addToProject")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSortedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {t("noUsersFound")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add to Project Dialog */}
      <Dialog open={isAddToProjectOpen} onOpenChange={setIsAddToProjectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("addToProject")}</DialogTitle>
            <DialogDescription>
              {selectedUserForProject && (
                <>
                  {t("addTeamMemberDesc")} ({selectedUserForProject.firstName} {selectedUserForProject.lastName})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t("selectProject")}</label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectProject")} />
                </SelectTrigger>
                <SelectContent>
                  {mockProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="useGlobalDefaults"
                checked={useGlobalDefaults}
                onCheckedChange={(checked) => setUseGlobalDefaults(checked as boolean)}
              />
              <label htmlFor="useGlobalDefaults" className="text-sm">
                {t("useGlobalDefaults")}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddToProjectOpen(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleAddToProject} disabled={!selectedProjectId}>
              {t("addToProject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default UsersRoles;
